<template>
	<div v-if="hasContent" class="space-y-10" data-testid="ai-verdict-insights">
		<section
			v-for="section in visibleSections"
			:key="section.key"
			class="space-y-3"
			:data-testid="`ai-verdict-section-${section.key}`"
		>
			<div class="flex items-center w-full gap-3">
				<Component
					:is="section.icon"
					size="20"
					class="shrink-0"
					:class="section.accentClass"
				/>
				<h5
					:id="section.headingId"
					class="flex-1 mb-0 text-base font-extrabold uppercase font-heading leading-[normal]"
					:class="section.accentClass"
				>
					{{ $t(section.headingKey) }}
				</h5>
				<button
					v-if="!isExporting"
					:id="section.toggleId"
					type="button"
					class="self-stretch flex items-center min-h-6 text-xs text-grey-300 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
					:aria-expanded="areAllExpanded(section) ? 'true' : 'false'"
					:aria-labelledby="`${section.headingId} ${section.toggleId}`"
					data-testid="ai-verdict-section-toggle"
					@click="toggleSection(section)"
				>
					{{ $t(sectionToggleKey(section)) }}
				</button>
			</div>

			<button
				v-for="(item, index) in section.items"
				:key="`${section.key}-${index}`"
				type="button"
				class="flex flex-col items-start w-full gap-1 p-4 text-left rounded-lg bg-grey-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
				:aria-expanded="isExpanded(section.key, index) ? 'true' : 'false'"
				data-testid="ai-verdict-card"
				@click="toggle(section.key, index)"
			>
				<span class="flex items-center justify-between w-full space-x-4">
					<span class="font-medium text-white">{{ item.title }}</span>
					<Icon
						:icon="[
							'far',
							isExpanded(section.key, index) ? 'minus' : 'plus'
						]"
						class="w-4 h-4 text-neutral-400 shrink-0"
					/>
				</span>

				<span
					v-if="isExpanded(section.key, index)"
					class="block text-sm text-neutral-300"
					data-testid="ai-verdict-card-content"
				>
					{{ item.content }}
				</span>
			</button>
		</section>
	</div>
</template>

<script>
import Icon from '~/components/ui/common/Icon'
import { IconAlertTriangle, IconCheckCircle } from '~/components/Icons'

export default {
	name: 'AiVerdictInsights',
	components: {
		Icon,
		IconAlertTriangle,
		IconCheckCircle
	},
	inject: {
		personalityResultsExport: { default: null }
	},
	props: {
		// Card arrays of {title, content}; counts are variable by design (2-4).
		whyFit: {
			type: Array,
			default: () => []
		},
		whatToWatch: {
			type: Array,
			default: () => []
		},
		// Which verdict perspective is shown — drives the "they" vs "you" heading.
		side: {
			type: String,
			default: 'company',
			validator(value) {
				return ['company', 'applicant'].includes(value)
			}
		}
	},
	data() {
		return {
			toggled: {}
		}
	},
	watch: {
		// Candidate switches can replace these props without remounting (batched
		// null→new verdict). Clear expanded rows so UI and open-emits stay visit-scoped.
		whyFit: 'resetExpanded',
		whatToWatch: 'resetExpanded'
	},
	computed: {
		isExporting() {
			return Boolean(this.personalityResultsExport?.exporting)
		},
		hasContent() {
			return this.whyFit.length > 0 || this.whatToWatch.length > 0
		},
		visibleSections() {
			return [
				{
					key: 'why-fit',
					items: this.whyFit,
					icon: IconCheckCircle,
					accentClass: 'text-green-400',
					headingId: `ai-verdict-heading-${this._uid}-why-fit`,
					toggleId: `ai-verdict-section-toggle-${this._uid}-why-fit`,
					headingKey:
						this.side === 'applicant'
							? 'personality_results.ai_verdict.why_thrive_applicant'
							: 'personality_results.ai_verdict.why_thrive'
				},
				{
					key: 'what-to-watch',
					items: this.whatToWatch,
					icon: IconAlertTriangle,
					accentClass: 'text-yellow-400',
					headingId: `ai-verdict-heading-${this._uid}-what-to-watch`,
					toggleId: `ai-verdict-section-toggle-${this._uid}-what-to-watch`,
					headingKey:
						'personality_results.ai_verdict.potential_challenges'
				}
			].filter(section => section.items.length > 0)
		}
	},
	methods: {
		resetExpanded() {
			this.toggled = {}
		},
		isExpanded(sectionKey, index) {
			// Image export renders every card open; the user's toggles stay put.
			if (this.personalityResultsExport?.exporting) {
				return true
			}
			return this.toggled[`${sectionKey}-${index}`] ?? false
		},
		// Product analytics keys for candidate_decision_made.
		analyticsSectionKey(sectionKey) {
			if (sectionKey === 'why-fit') return 'why_fit'
			if (sectionKey === 'what-to-watch') return 'what_to_watch'
			return sectionKey
		},
		areAllExpanded(section) {
			return section.items.every((_, index) =>
				this.isExpanded(section.key, index)
			)
		},
		sectionToggleKey(section) {
			return this.areAllExpanded(section)
				? 'personality_results.ai_verdict.collapse_all'
				: 'personality_results.ai_verdict.expand_all'
		},
		toggleSection(section) {
			const expand = !this.areAllExpanded(section)
			section.items.forEach((_, index) => {
				if (this.isExpanded(section.key, index) === expand) {
					return
				}
				this.toggle(section.key, index)
			})
		},
		toggle(sectionKey, index) {
			const willExpand = !this.isExpanded(sectionKey, index)
			this.$set(this.toggled, `${sectionKey}-${index}`, willExpand)
			// Open-only: collapses do not clear visit-scoped engagement.
			if (willExpand) {
				this.$emit('insight-opened', {
					section: this.analyticsSectionKey(sectionKey),
					index
				})
			}
		}
	}
}
</script>
